import React from "react";
import {
  IonButton,
  IonButtons,
  IonCol,
  IonContent,
  IonFooter,
  IonGrid,
  IonHeader,
  IonItem,
  IonItemDivider,
  IonItemGroup,
  IonLabel,
  IonPage,
  IonRange,
  IonRow,
  IonSegment,
  IonSegmentButton,
  IonSelect,
  IonSelectOption,
  IonTitle,
  IonToggle,
  IonToolbar,
  isPlatform,
} from "@ionic/react";
import { SpikeGenProps } from "../../interfaces/core";
import { presetColors } from "../../lib/spike-pie/colors";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

const SpikeGenSettings: React.FC<{
  inputs: SpikeGenProps;
  resetForm: () => void;
  handleChange: (e) => void;
  handleDismiss: () => void;
  handleSaveSettings: () => void;
  handleResetDefaultSettings: () => void;
}> = ({
  inputs,
  resetForm,
  handleChange,
  handleDismiss,
  handleSaveSettings,
  handleResetDefaultSettings,
}) => {
  const { t } = useTranslation();
  const checkInputs = () => {
    if (Object.keys(inputs).length === 0) {
      return true;
    } else {
      return false;
    }
  };
  const isMobile = isPlatform("mobile");
  const handleSelectedColors = useCallback(
    (e) => {
      let _selectedColors = inputs.selectedColors;
      const { name } = e.target;
      const alreadySelected = _selectedColors.findIndex((c) => c === name);
      // if no matched name is found from selected colors
      if (alreadySelected === -1) {
        // Add to array
        _selectedColors = [..._selectedColors, name];
      } else {
        _selectedColors = _selectedColors.filter((c) => c !== name);
      }
      handleChange({
        detail: { value: _selectedColors, name: "selectedColors" },
      });
    },
    [handleChange]
  );

  const selectedAll = inputs.selectedColors.length === presetColors.length;
  const imageSize =
    Math.round(
      ((inputs.tileX *
        inputs.tileY *
        (50 +
          (inputs.colorType === "multiColor" ? 15 : 0) +
          (inputs.colorGradient ? (inputs.scale > 9 ? 10 : 5) : 0)) *
        Math.pow(
          inputs.scale,
          inputs.scale > 9 ? 1.22 : inputs.scale > 6 ? 1.25 : 1.28
        )) /
        1024) *
        100
    ) / 100;
  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="primary">
            <IonButton onClick={handleDismiss} className="!font-semibold">
              {t(`Done`)}
            </IonButton>
          </IonButtons>
          <IonTitle>{t(`Customize`)}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div className={`m-4`}>
          <IonItemGroup className="item-group-input !bg-transparent !mb-2">
            <IonItemDivider className="pl-0 !bg-transparent">
              {t(`Aspect Ratio`)}
            </IonItemDivider>
            <IonGrid className="px-0">
              <IonRow>
                <IonCol className="pl-0 mr-1">
                  <IonItem className="item-input">
                    <IonLabel>{t(`Spike Pie Generator.X`)}</IonLabel>
                    <IonSelect
                      slot="end"
                      name="tileX"
                      value={inputs.tileX}
                      placeholder={t(`Select One`)}
                      onIonChange={handleChange}
                      interface="popover"
                    >
                      {[...Array(4).keys()].map((key) => (
                        <IonSelectOption
                          key={`numofimg.${key}`}
                          value={key + 1}
                        >
                          {key + 1}
                        </IonSelectOption>
                      ))}
                    </IonSelect>
                  </IonItem>
                </IonCol>
                <IonCol className="pr-0 ml-1">
                  <IonItem className="item-input">
                    <IonLabel>{t(`Spike Pie Generator.Y`)}</IonLabel>
                    <IonSelect
                      slot="end"
                      name="tileY"
                      value={inputs.tileY}
                      placeholder={t(`Select One`)}
                      onIonChange={handleChange}
                      interface="popover"
                    >
                      {[...Array(4).keys()].map((key) => (
                        <IonSelectOption
                          key={`numofimg.${key}`}
                          value={key + 1}
                        >
                          {key + 1}
                        </IonSelectOption>
                      ))}
                    </IonSelect>
                  </IonItem>
                </IonCol>
              </IonRow>
            </IonGrid>
          </IonItemGroup>
          <IonItemGroup className="item-group-input !bg-transparent">
            <IonItemDivider className="pl-0 pb-2 !bg-transparent">
              {t(`Color`)}
            </IonItemDivider>
            <IonItem className="item-input">
              <IonLabel>{t(`Type`)}</IonLabel>
              <IonSelect
                slot="end"
                name="colorType"
                value={inputs.colorType}
                placeholder={t(`Select One`)}
                onIonChange={handleChange}
                interface="popover"
              >
                <IonSelectOption value="solid">{t(`Solid`)}</IonSelectOption>
                <IonSelectOption value="multiColor">
                  {t(`Multi Color`)}
                </IonSelectOption>
              </IonSelect>
            </IonItem>
            <IonItem className="item-input">
              <IonLabel slot="start">{t(`Gradient Color`)}</IonLabel>

              <IonToggle
                slot="end"
                name={"colorGradient"}
                checked={inputs.colorGradient}
                onIonChange={handleChange}
              />
            </IonItem>
            <IonItem className="item-input">
              <IonLabel slot="start">{t(`Random Colors`)}</IonLabel>
              <IonToggle
                slot="end"
                name={"randomColors"}
                checked={inputs.randomColors}
                onIonChange={handleChange}
              />
            </IonItem>
            {!inputs.randomColors && (
              <>
                <IonItem className="item-input">
                  <div className="flex-grow pr-2">
                    <IonButton
                      expand="block"
                      color="secondary"
                      size="small"
                      onClick={() =>
                        selectedAll
                          ? handleChange({
                              target: { value: [], name: "selectedColors" },
                            })
                          : handleChange({
                              target: {
                                value: presetColors.map((c) => c.name),
                                name: "selectedColors",
                              },
                            })
                      }
                    >
                      {selectedAll ? t("Unselect All") : t("Select All")}
                    </IonButton>
                    <IonSegment
                      className="mt-2"
                      onIonChange={(e) =>
                        handleChange({
                          target: {
                            value: e.detail.value,
                            name: "detailedColorView",
                          },
                        })
                      }
                      value={inputs.detailedColorView}
                      defaultValue={"primary"}
                    >
                      <IonSegmentButton value="primary">
                        <IonLabel>{t(`Theme Color`)}</IonLabel>
                      </IonSegmentButton>
                      <IonSegmentButton value="palette">
                        <IonLabel>{t(`Color Palette`)}</IonLabel>
                      </IonSegmentButton>
                    </IonSegment>
                  </div>
                </IonItem>
                <IonItem className="item-input">
                  <div className="flex-grow pr-2">
                    <div className="py-2"></div>
                    <div
                      className={`flex justify-center items-center my-2 flex-wrap ${
                        inputs.detailedColorView === "primary"
                          ? "gap-4"
                          : "gap-2 flex-col"
                      }`}
                    >
                      {presetColors.map(({ FG, BG, name }, i) => {
                        const checked =
                          selectedAll ||
                          inputs.selectedColors.filter((c) => c === name)
                            .length > 0;

                        return inputs.detailedColorView === "primary" ? (
                          <PrimaryColor
                            key={`primary.${name}`}
                            {...{
                              checked,
                              FG,
                              BG,
                              name,
                              handleSelectedColors,
                              inputs,
                            }}
                          />
                        ) : (
                          <PaletteColor
                            key={`palette.${name}`}
                            {...{
                              checked,
                              FG,
                              BG,
                              name,
                              handleSelectedColors,
                              inputs,
                            }}
                          />
                        );
                      })}
                    </div>
                  </div>
                </IonItem>
              </>
            )}
          </IonItemGroup>
          <IonItemGroup className="item-group-input !bg-transparent">
            <IonItemDivider className="pl-0 pb-2 !bg-transparent">
              {t(`Output`)}
            </IonItemDivider>
            <IonItem className="item-input">
              <IonLabel>{t(`Number of images`)}</IonLabel>
              <IonSelect
                slot="end"
                name="numberOfImages"
                value={inputs.numberOfImages}
                placeholder={t(`Select One`)}
                onIonChange={handleChange}
                interface="popover"
              >
                {[...Array(isMobile ? 5 : 10).keys()].map((key) => (
                  <IonSelectOption key={`numofimg.${key}`} value={key + 1}>
                    {key + 1}
                  </IonSelectOption>
                ))}
              </IonSelect>
            </IonItem>
            <IonItem className="item-input">
              <IonLabel>{t(`Resolution`)}</IonLabel>
              <IonRange
                name="scale"
                value={inputs.scale}
                min={1}
                max={isMobile ? 4 : 8}
                step={1}
                onIonChange={handleChange}
                snaps
                ticks
              />
            </IonItem>
          </IonItemGroup>
          <div className="item-description">
            <p>
              {t(`Image resolution:`)} {inputs.tileX * 400 * inputs.scale} x{" "}
              {inputs.tileY * 400 * inputs.scale} px
            </p>
            <p>
              {t(`Estimated size per image:`)} {imageSize} MB
            </p>
            <p>
              {t(`Estimated total size:`)}{" "}
              {Math.round(imageSize * inputs.numberOfImages * 100) / 100} MB
            </p>
          </div>
          <IonItemGroup className="item-group-input !bg-transparent pt-8">
            <IonItem
              className="item-input"
              onClick={handleResetDefaultSettings}
              button
              detail={false}
            >
              <IonLabel color="primary">{t(`Reset to default`)}</IonLabel>
            </IonItem>
          </IonItemGroup>
        </div>
      </IonContent>
      <IonFooter>
        <IonToolbar className="!px-0">
          <div className="grid grid-cols-2 gap-2 px-2">
            <IonButton
              className="font-semibold"
              expand="block"
              onClick={() => {
                resetForm();
                handleDismiss();
              }}
              color="secondary"
            >
              {t(`Cancel`)}
            </IonButton>
            <IonButton
              className="font-semibold"
              expand="block"
              onClick={handleSaveSettings}
              disabled={checkInputs()}
            >
              {t(`Save`)}
            </IonButton>
          </div>
        </IonToolbar>
      </IonFooter>
    </IonPage>
  );
};
const PrimaryColor = ({
  checked,
  FG,
  BG,
  name,
  handleSelectedColors,
  inputs,
}) => (
  <label className="flex-row flex-wrap" htmlFor={`primary.input.${name}`}>
    <input
      name={name}
      type="checkbox"
      id={`primary.input.${name}`}
      checked={checked}
      onChange={handleSelectedColors}
      aria-hidden="true"
      className="hidden"
    />
    <div
      className={`rounded-full mb-2 w-12 h-12 mx-auto flex justify-center items-center`}
      style={{
        background: inputs.colorGradient
          ? `linear-gradient(180deg, ${FG[2]} 0%, ${FG[5]} 100%)`
          : FG[4],
      }}
    >
      {checked && (
        <div className="w-full h-full p-[3px]">
          <div className="ring-2 w-full h-full rounded-full ring-inset ring-black"></div>
        </div>
      )}
    </div>
  </label>
);
const PaletteColor = ({
  checked,
  FG,
  BG,
  name,
  handleSelectedColors,
  inputs,
}) => (
  <label
    className="w-full flex flex-col rounded-md overflow-hidden mb-2 relative"
    htmlFor={`palette.input.${name}`}
  >
    <input
      name={name}
      type="checkbox"
      id={`palette.input.${name}`}
      checked={checked}
      onChange={handleSelectedColors}
      aria-hidden="true"
      className="hidden"
    />
    {checked && (
      <div className="w-full h-full p-[3px] absolute">
        <div className="ring-2 w-full h-full rounded-md ring-inset ring-black"></div>
      </div>
    )}
    <div className="flex w-full">
      {FG.map((f, i) => (
        <div
          key={`palette.${name}.FG.${f}`}
          className={`h-12 w-full mx-auto flex justify-center items-center`}
          style={{
            background: inputs.colorGradient
              ? `linear-gradient(90deg, ${f} 0%, ${
                  i + 1 < FG.length ? FG[i + 1] : FG[i]
                } 100%)`
              : f,
          }}
        ></div>
      ))}
    </div>

    <div className="flex w-full">
      {BG.map((b, i) => (
        <div
          key={`palette.${name}.BG.${b}.${i}`}
          className={`h-6 w-full mx-auto flex justify-center items-center`}
          style={{
            background: inputs.colorGradient
              ? `linear-gradient(90deg, ${b} 0%, ${
                  i + 1 < BG.length ? BG[i + 1] : BG[i]
                } 100%)`
              : b,
          }}
        ></div>
      ))}
    </div>
    {/* <p className="text-[0.6rem] opacity-70 text-center uppercase font-medium">
                          {name}
                        </p> */}
  </label>
);

export default SpikeGenSettings;
